// 로그인 페이지 — poc-portal LoginPage 이식판 (react-router 제거: 로그인 성공 시 인증 상태 변화로 App이 전환)
import React, { useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { readSavedUsername, rememberUsername } from '../../lib/savedLogin';
import { useAuth } from '../../context/AuthContext';
import { AXButton } from 'yukimushi-component-fe';
import styles from './LoginPage.module.css';

export default function LoginPage() {
  const { loginWithCredentials } = useAuth();

  /* 아이디만 기억한다 — 비밀번호는 저장하지 않는다(H-04). 옛 형식이 남아 있으면 읽는 순간 비밀번호가 지워진다 */
  const saved = useRef(readSavedUsername(localStorage)).current;
  const [username, setUsername] = useState(saved ?? '');
  const [password, setPassword] = useState('');
  const [autoLogin, setAutoLogin] = useState(!!saved);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // 체크박스 최신 값을 자동 로그인 effect에서도 참조하기 위한 ref
  const autoLoginRef = useRef(autoLogin);
  autoLoginRef.current = autoLogin;

  const doLogin = async (creds: { username: string; password: string }) => {
    setError(null);
    setLoading(true);
    try {
      await loginWithCredentials(creds);
      rememberUsername(localStorage, creds.username, autoLoginRef.current);
      // 라우터 없음 — 인증 상태가 바뀌면 App이 Layout으로 전환한다
    } catch (err) {
      setError(err instanceof Error && /[가-힣]/.test(err.message) ? err.message : '로그인하지 못했습니다. 잠시 후 다시 시도하세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await doLogin({ username, password });
  };

  return (
    <div className={styles.page}>
      {/* ── 좌측 브랜드 히어로 패널 (좁은 화면에선 숨김) ── */}
      <aside className={styles.hero}>
        <div className={styles.heroTop}>
          <div className={styles.logoMark}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <rect x="1" y="1" width="9" height="9" rx="2" fill="white" fillOpacity=".9" />
              <rect x="12" y="1" width="9" height="9" rx="2" fill="white" fillOpacity=".6" />
              <rect x="1" y="12" width="9" height="9" rx="2" fill="white" fillOpacity=".6" />
              <rect x="12" y="12" width="9" height="9" rx="2" fill="white" fillOpacity=".3" />
            </svg>
          </div>
          <span className={styles.wordmark}>
            <span className={styles.wordmarkMain}>Enterprise AI</span>
            <span className={styles.wordmarkSub}>Platform</span>
          </span>
        </div>

        <div className={styles.heroBody}>
          <h1 className={styles.heroTitle}>
            기업의 업무 흐름을 하나로 연결하는,
            <br />
            차세대 비즈니스 Enterprise AI
          </h1>
          <p className={styles.heroDesc}>
            당신의 모든 순간에 AI에이전트가 함께합니다.
          </p>
        </div>
      </aside>

      {/* ── 우측 로그인 폼 패널 ── */}
      <main className={styles.formPanel}>
        <div className={styles.formInner}>
          <div className={styles.mobileBrand}>
            <div className={styles.logoMark}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <rect x="1" y="1" width="9" height="9" rx="2" fill="white" fillOpacity=".9" />
                <rect x="12" y="1" width="9" height="9" rx="2" fill="white" fillOpacity=".6" />
                <rect x="1" y="12" width="9" height="9" rx="2" fill="white" fillOpacity=".6" />
                <rect x="12" y="12" width="9" height="9" rx="2" fill="white" fillOpacity=".3" />
              </svg>
            </div>
            <span className={styles.wordmark}>
              <span className={styles.wordmarkMain}>Enterprise AI</span>
              <span className={styles.wordmarkSub}>Platform</span>
            </span>
          </div>

          <h2 className={styles.title}>로그인</h2>
          <p className={styles.subtitle}>계정 정보를 입력해 주세요.</p>

          <form className={styles.form} onSubmit={handleSubmit}>
            <label className={styles.field}>
              <span className={styles.label}>아이디</span>
              <input
                className={styles.input}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="아이디를 입력하세요"
                autoComplete="username"
                autoFocus
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>비밀번호</span>
              <input
                className={styles.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호를 입력하세요"
                autoComplete="current-password"
              />
            </label>

            <label className={styles.checkboxRow}>
              <input
                className={styles.checkbox}
                type="checkbox"
                checked={autoLogin}
                onChange={(e) => setAutoLogin(e.target.checked)}
              />
              <span>아이디 저장</span>
            </label>

            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}

            <AXButton type="submit" variant="primary" size="lg" fullWidth disabled={loading}>
              {loading ? '로그인 중…' : '로그인'}
            </AXButton>
          </form>

          {process.env.REACT_APP_AUTH_MODE === 'mock' && (
            <p className={styles.hint}>
              데모(mock) 모드입니다. 아이디/비밀번호를 입력하면 로그인됩니다.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
